import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

import { FOCUS_RING_INSET } from "../lib/focus";
import { cn } from "../lib/utils";
import { Badge } from "./badge";
import { Icon, type IconComponent } from "./icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * v2 navigation pieces (redesign phase 5, #455): NavSection and NavItem.
 * Storybook only until implementation. The container (sidebar, rail width,
 * `<nav>`, logo, collapse button, scrolling) is the app frame, designed at
 * implementation; these sit inside it.
 *
 * NavItem (break #98): 36 high, padding 12, radius 6, Sora 14 Regular `--ink`,
 * icon 16 `--ink-muted`, overlay hover, inset focus ring (items sit 4px apart).
 * Active = `--primary-subtle` + `--primary-strong` (9.23 / 7.43) + Semibold, the
 * same selection language as rows and menus, with `aria-current="page"`. The
 * weight is the cue that isn't colour. Renders the caller's link through
 * `asChild` (router-agnostic), else a button.
 *
 *   count       `xs` neutral Badge on the right, `99+` above 99
 *   status      8px dot in the badge tone's text colour (break #99), hollow for
 *               neutral (offline); its label is screen-reader text and part
 *               of the collapsed tooltip
 *   disabled    stays focusable (`aria-disabled`, no link) so keyboard users
 *               reach `disabledReason`, which is the description and a tooltip
 *   depth 1     indented to the parent's label (Ontologie's children)
 *
 * Expanded, a label cut by the sidebar's width (e.g. "Wissensaufbau-Pipeline"
 * under the staff rule at 240) shows the full label as a tooltip to the right,
 * only while it is cut (the Chip approach, break #154). Not a description: the
 * label is the name already.
 *
 * Collapsed (rail): 36 square, icon only; the label is screen-reader text and
 * a tooltip to the right (the global TooltipProvider's delay; moving along
 * the rail skips it). Count and status stay in the name and the tooltip; the
 * status dot sits on the icon's corner.
 *
 * NavSection: an optional heading (12 Semibold `--ink-muted`, sentence case)
 * naming a `role="group"`. Collapsed, the heading is screen-reader text and a
 * short rule marks the section.
 *
 * Staff (break #100, DESIGN.md: Staff Orange "exclusively for lytra-staff-only
 * function indicators"): the heading stays `--ink-muted`; orange (`--staff`)
 * appears only as the marker, a 2px rule left of the items (expanded) or
 * above them (collapsed). `--staff` is 2.80:1 on a light surface (6.97 dark),
 * below text and 3:1 minimums, so it never carries text. Expanded, the heading
 * says "staff"; collapsed, the rule is the only visual cue in light mode
 * (accepted 05.10, listed with break #100). Items in the section get no tint.
 */

/** Badge tones that read as a dot; `warning-strong` (olive #4F5105) does not, so it is left out. */
export type NavStatusTone = "success" | "neutral" | "error";

/**
 * Filled for success and error, a hollow ring for neutral: at 8px the dark
 * `--success-strong` and `--ink-muted` are hard to tell apart in light mode,
 * so online / offline differ in shape, not only in colour.
 */
const STATUS_DOT: Record<NavStatusTone, string> = {
  success: "bg-success-strong",
  neutral: "border-2 border-ink-muted",
  error: "bg-destructive-strong",
};

const NavContext = React.createContext<{ collapsed: boolean }>({ collapsed: false });

/**
 * Set by the app frame's Sidebar (rail vs expanded), so its NavSections don't
 * repeat `collapsed`. A section's own `collapsed` prop still wins.
 */
const NavCollapsedContext = React.createContext<boolean | undefined>(undefined);

export interface NavSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Section heading. Visible when expanded, screen-reader text when collapsed. */
  label?: string;
  /** lytra-staff-only section: orange marker rule (never orange text). */
  staff?: boolean;
  collapsed?: boolean;
}

function NavSection({ label, staff = false, collapsed: collapsedProp, className, children, ...props }: NavSectionProps) {
  const id = React.useId();
  const frameCollapsed = React.useContext(NavCollapsedContext);
  const collapsed = collapsedProp ?? frameCollapsed ?? false;
  return (
    <NavContext.Provider value={{ collapsed }}>
      <div
        role={label ? "group" : undefined}
        aria-labelledby={label ? id : undefined}
        data-staff={staff || undefined}
        className={cn("flex flex-col", collapsed && "items-center", className)}
        {...props}
      >
        {label && (
          <div id={id} className={collapsed ? "sr-only" : "px-3 pb-1 font-sans text-caption font-semibold text-ink-muted"}>
            {label}
          </div>
        )}
        {collapsed && (label || staff) && (
          <div
            aria-hidden="true"
            data-slot="section-rule"
            className={cn("mb-2 w-5 rounded-full", staff ? "h-0.5 bg-staff" : "h-px bg-divider")}
          />
        )}
        <ul
          role="list"
          className={cn(
            "flex flex-col gap-1",
            collapsed ? "items-center" : "w-full",
            staff && !collapsed && "border-l-2 border-staff pl-2",
          )}
        >
          {children}
        </ul>
      </div>
    </NavContext.Provider>
  );
}

export interface NavItemProps extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  icon: IconComponent;
  /** Visible label; the accessible name and, collapsed, the tooltip. German UI text. */
  label: string;
  /** Current page: tint, Semibold, `aria-current="page"`. */
  active?: boolean;
  count?: number;
  status?: { tone: NavStatusTone; label: string };
  disabled?: boolean;
  /** Why it is disabled: description + tooltip. */
  disabledReason?: string;
  depth?: 0 | 1;
  /** Overrides the section's collapsed state. */
  collapsed?: boolean;
  /** Render the single child element (a router link) as the item. */
  asChild?: boolean;
  children?: React.ReactElement;
}

/** "99+" above 99, German digits. Shared with SegmentedControlItem's `count`. */
export const formatCount = (count: number) => (count > 99 ? "99+" : count.toLocaleString("de-DE"));

const NavItem = React.forwardRef<HTMLElement, NavItemProps>(function NavItem(
  {
    icon,
    label,
    active = false,
    count,
    status,
    disabled = false,
    disabledReason,
    depth = 0,
    collapsed: collapsedProp,
    asChild = false,
    className,
    children,
    ...props
  },
  ref,
) {
  const section = React.useContext(NavContext);
  const collapsed = collapsedProp ?? section.collapsed;
  const reasonId = React.useId();
  const labelRef = React.useRef<HTMLSpanElement>(null);
  // Expanded: whether the label was cut when the tooltip asked to open (measured then, as Chip does).
  const [cutOpen, setCutOpen] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const className_ = cn(
    "group/item relative flex h-9 min-w-0 shrink-0 cursor-pointer items-center gap-2 rounded-md font-sans text-body text-ink outline-hidden transition-colors",
    FOCUS_RING_INSET,
    collapsed ? "w-9 justify-center" : cn("w-full px-3", depth === 1 && "pl-9"),
    active
      ? "bg-primary-subtle font-semibold text-primary-strong"
      : disabled
        ? "cursor-not-allowed opacity-50"
        : "hover:bg-hover-overlay data-hover:bg-hover-overlay",
    className,
  );

  const dot = status ? (
    <span
      aria-hidden="true"
      data-slot="status-dot"
      className={cn(
        "size-2 shrink-0 rounded-full",
        STATUS_DOT[status.tone],
        collapsed && "absolute right-1 top-1",
      )}
    />
  ) : null;

  const content = (
    <>
      <Icon icon={icon} className={active ? undefined : "text-ink-muted"} />
      <span ref={labelRef} className={collapsed ? "sr-only" : "min-w-0 truncate"}>
        {label}
      </span>
      {dot}
      {status && <span className="sr-only">, {status.label}</span>}
      {count != null &&
        (collapsed ? (
          <span className="sr-only">, {formatCount(count)}</span>
        ) : (
          <>
            {" "}
            <Badge size="xs" className="ml-auto tabular-nums">
              {formatCount(count)}
            </Badge>
          </>
        ))}
    </>
  );

  // Outside the item: as a child it would join the accessible name.
  const reasonText =
    disabled && disabledReason ? (
      <span id={reasonId} className="sr-only">
        {disabledReason}
      </span>
    ) : null;

  const shared = {
    ...props,
    ref,
    className: className_,
    "aria-current": active ? ("page" as const) : undefined,
    // The selected-row contract (`group/item` + `data-selected`): the count Badge draws its ring on the tint (break #188).
    "data-selected": active || undefined,
  };

  let element: React.ReactElement;
  if (disabled) {
    // Not a link any more, but still reachable, so the reason can be read.
    element = (
      <span
        {...shared}
        ref={ref as React.Ref<HTMLSpanElement>}
        role="link"
        aria-disabled="true"
        aria-describedby={disabledReason ? reasonId : undefined}
        tabIndex={0}
      >
        {content}
      </span>
    );
  } else if (asChild && React.isValidElement(children)) {
    element = <Slot {...shared}>{React.cloneElement(children, undefined, content)}</Slot>;
  } else {
    element = (
      <button {...shared} ref={ref as React.Ref<HTMLButtonElement>} type="button">
        {content}
      </button>
    );
  }

  const reason = disabled ? disabledReason : undefined;
  // Collapsed: always "Label · Anzahl · Status". Expanded: the label only while it is cut.
  const tip = collapsed
    ? [label, count != null ? formatCount(count) : null, status?.label].filter(Boolean).join(" · ")
    : cutOpen
      ? label
      : null;

  return (
    <li className={collapsed ? "flex justify-center" : "flex"}>
      <Tooltip
        open={tooltipOpen}
        onOpenChange={(open) => {
          const el = labelRef.current;
          const cut = open && !collapsed && !!el && el.scrollWidth > el.clientWidth;
          setCutOpen(cut);
          setTooltipOpen(open && (collapsed || !!reason || cut));
        }}
      >
        {/* Not a description: the name already holds the label, and the reason has its own id. */}
        <TooltipTrigger asChild aria-describedby={reason ? reasonId : undefined}>
          {element}
        </TooltipTrigger>
        <TooltipContent side="right">
          {tip}
          {tip && reason && <br />}
          {reason}
        </TooltipContent>
      </Tooltip>
      {reasonText}
    </li>
  );
});

export { NavSection, NavItem, NavCollapsedContext };
